<template>
  <!-- Vista previa de una planilla (Excel/CSV) dentro de DraftCard: resumen,
       columnas detectadas, avisos del archivo, grupos (consumos) y filas.
       Contrato § 4 · kinds `staff_roster` y `consumption_log`. Los cambios se
       emiten como el body del PATCH; DraftCard los manda. -->
  <div class="ds">
    <!-- Resumen -->
    <p class="ds-summary">{{ summaryText }}</p>

    <!-- Columnas detectadas -->
    <details v-if="mappingEntries.length" class="ds-box">
      <summary>{{ $t('assistant.sheet.mapping', { n: mappingEntries.length }) }}</summary>
      <ul class="ds-mapping">
        <li v-for="m in mappingEntries" :key="m.field">
          <span class="ds-mapping__col">{{ m.header || '—' }}</span>
          <PhArrowRight :size="12" />
          <span class="ds-mapping__field">{{ fieldLabel(m.field) }}</span>
          <DsSelect
            v-if="!readOnly && columns.length"
            :wide="false"
            class="ds-mapping__select"
            :model-value="m.index == null ? '' : String(m.index)"
            :aria-label="fieldLabel(m.field)"
            :disabled="busy"
            @update:model-value="(v) => $emit('patch', { mapping: { [m.field]: v === '' ? null : Number(v) } })"
          >
            <option value="">{{ $t('assistant.sheet.noColumn') }}</option>
            <option v-for="c in columns" :key="c.value" :value="String(c.value)">{{ c.label }}</option>
          </DsSelect>
        </li>
      </ul>
    </details>

    <!-- Avisos del archivo -->
    <ul v-if="draft.warnings?.length" class="ds-warnings">
      <li v-for="(w, i) in draft.warnings" :key="i" :class="{ 'is-strong': STRONG.has(w.code) }">
        <PhWarning :size="14" weight="fill" /> <span>{{ w.message }}</span>
      </li>
    </ul>

    <!-- Grupos (planilla de consumos): destino por grupo -->
    <ul v-if="groups.length" class="ds-groups">
      <li v-for="g in groups" :key="g.gid" class="ds-group" :class="{ 'is-missing': g.missing?.length }">
        <div class="ds-group__head">
          <span class="ds-group__label">{{ g.label }}</span>
          <span class="ds-chip" :class="g.missing?.length ? 'is-warning' : 'is-ok'">
            {{ g.missing?.length ? $t('assistant.sheet.groupMissing') : $t('assistant.sheet.groupReady') }}
          </span>
        </div>
        <span class="ds-group__meta">
          {{ $t('assistant.sheet.rows', { n: g.rowCount }, g.rowCount) }}<template v-if="groupTarget(g)"> · {{ groupTarget(g) }}</template>
        </span>
        <template v-if="!readOnly">
          <form v-if="editingGroup === g.gid" class="ds-group__edit" @submit.prevent="saveGroup(g)">
            <label v-for="f in groupFields(g)" :key="f.key">
              {{ $t(`assistant.fields.${f.label}`) }}
              <DsSelect v-model="groupEdit[f.key]">
                <option value="">—</option>
                <option v-for="o in f.options" :key="String(o.value)" :value="String(o.value)">{{ o.label }}</option>
              </DsSelect>
            </label>
            <div class="ds-actions">
              <DsButton type="submit" size="sm" :disabled="busy">{{ $t('assistant.draft.save') }}</DsButton>
              <DsButton variant="secondary" size="sm" @click="editingGroup = null">{{ $t('assistant.draft.cancel') }}</DsButton>
            </div>
          </form>
          <button v-else-if="groupFields(g).length" type="button" class="ds-link" :disabled="busy" @click="startGroup(g)">
            {{ $t('assistant.sheet.changeTarget') }}
          </button>
        </template>
      </li>
    </ul>

    <!-- Filas: lista compacta (una tabla no entra en el ancho del panel) -->
    <ul v-if="rows.length" class="ds-rows">
      <li v-for="r in shownRows" :key="r.index" class="ds-row" :class="`is-${r.status}`">
        <div class="ds-row__head">
          <span class="ds-num" :title="$t('assistant.sheet.row')">{{ r.column || r.rowNumber }}</span>
          <span class="ds-main">
            <span class="ds-main__primary">{{ rowPrimary(r) }}</span>
            <span v-if="rowSecondary(r)" class="ds-main__secondary">{{ rowSecondary(r) }}</span>
          </span>
          <button
            v-if="!readOnly"
            type="button"
            class="ds-link ds-row__act"
            :disabled="busy"
            @click="$emit('patch', r.status === 'excluded' ? { include: [r.index] } : { exclude: [r.index] })"
          >
            {{ r.status === 'excluded' ? $t('assistant.sheet.include') : $t('assistant.sheet.exclude') }}
          </button>
        </div>
        <div class="ds-row__chips">
          <span class="ds-chip" :class="`is-${r.status}`">{{ $t(`assistant.sheet.statuses.${r.status}`) }}</span>
          <span v-if="r.action" class="ds-chip is-action">{{ $t(`assistant.sheet.actionsKind.${r.action}`) }}</span>
        </div>
        <ul v-if="r.issues?.length" class="ds-row__issues">
          <li v-for="(is, k) in r.issues" :key="k" :class="`is-${is.severity}`">{{ is.message }}</li>
        </ul>
      </li>
    </ul>
    <button v-if="rows.length > LIMIT" type="button" class="ds-link" @click="showAll = !showAll">
      {{ showAll ? $t('assistant.sheet.showLess') : $t('assistant.sheet.showAll', { n: rows.length }) }}
    </button>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhWarning, PhArrowRight } from '@phosphor-icons/vue';
import DsButton from '@/components/ds/DsButton.vue';
import DsSelect from '@/components/ds/DsSelect.vue';

const LIMIT = 30;
// Avisos del archivo que conviene que la persona confirme (§ 4 planillas).
const STRONG = new Set(['inventory_inferred', 'target_inferred', 'possible_duplicates', 'rows_with_errors']);
const UNITS = { kWh: 'kWh', m3: 'm³', 'm³': 'm³', L: 'L' };

// Destino de un grupo de consumos: claves del PATCH `groups` y de dónde salen las opciones.
const GROUP_FIELDS = [
  { key: 'inventoryId', label: 'inventory', options: 'inventories' },
  { key: 'activity_id', label: 'activity', options: 'activities' },
  { key: 'sourceId', label: 'source', options: 'sources' },
  { key: 'vehicleActivityId', label: 'vehicle', options: 'vehicles' },
  { key: 'water_source', label: 'water_source', options: 'water_sources' },
];

export default {
  name: 'DraftSheet',
  components: { DsButton, DsSelect, PhWarning, PhArrowRight },
  props: {
    draft: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
  },
  emits: ['patch'],
  setup(props, { emit }) {
    const { t, te, locale } = useI18n();
    const showAll = ref(false);
    const editingGroup = ref(null);
    const groupEdit = reactive({});

    const reading = computed(() => props.draft.proposal?.reading || {});
    const options = computed(() => props.draft.proposal?.options || {});
    const rows = computed(() => reading.value.rows || []);
    const groups = computed(() => reading.value.groups || []);
    const shownRows = computed(() => (showAll.value ? rows.value : rows.value.slice(0, LIMIT)));
    const columns = computed(() => (options.value.columns || []).map((c, i) =>
      (typeof c === 'object' ? { value: c.value ?? c.index ?? i, label: c.label ?? c.header ?? String(c.value ?? i) } : { value: i, label: String(c) })));

    const num = (n) => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(n);
    const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
    const fieldLabel = (f) => (te(`assistant.sheet.fields.${f}`) ? t(`assistant.sheet.fields.${f}`) : f);
    const kindLabel = (k) => (te(`assistant.modules.monitor.kinds.${k}`) ? t(`assistant.modules.monitor.kinds.${k}`) : k);

    // "7 filas · 5 listas · 2 con problemas · 1 se actualiza"
    const summaryText = computed(() => {
      const s = reading.value.summary || {};
      const parts = [];
      const isLog = reading.value.doc_type === 'consumption_log';
      parts.push(t(isLog ? 'assistant.sheet.sumReadings' : 'assistant.sheet.sumRows', { n: s.total || 0 }, s.total || 0));
      if (isLog && s.byKind) {
        const byKind = Object.entries(s.byKind).filter(([, n]) => n).map(([k, n]) => `${n} ${kindLabel(k).toLowerCase()}`);
        if (byKind.length) parts.push(byKind.join(', '));
      }
      const ready = (s.ok || 0) + (s.warnings || 0);
      parts.push(t('assistant.sheet.sumReady', { n: ready }, ready));
      if (s.errors) parts.push(t('assistant.sheet.sumErrors', { n: s.errors }, s.errors));
      if (s.excluded) parts.push(t('assistant.sheet.sumExcluded', { n: s.excluded }, s.excluded));
      if (s.update) parts.push(t('assistant.sheet.sumUpdate', { n: s.update }, s.update));
      if (s.truncated) parts.push(t('assistant.sheet.sumTruncated'));
      return parts.join(' · ');
    });

    // mapping: { campo: "Encabezado del Excel" }; el índice sale de options.columns.
    const mappingEntries = computed(() => Object.entries(reading.value.mapping || {}).map(([field, header]) => {
      const col = columns.value.find(c => c.label === header);
      return { field, header, index: col ? col.value : null };
    }));

    // Fila: personal → "Nombre Apellidos" · puesto; consumos → fecha · tipo · consumo.
    const rowPrimary = (r) => {
      if (r.values) return [r.values.name, r.values.lastName].filter(Boolean).join(' ') || r.values.email || '—';
      const rd = r.reading || {};
      return [rd.consumption != null ? `${num(rd.consumption)} ${UNITS[rd.unit] || rd.unit || ''}`.trim() : null, rd.doc_type ? kindLabel(rd.doc_type) : null]
        .filter(Boolean).join(' · ') || '—';
    };
    const rowSecondary = (r) => {
      if (r.values) return [r.values.role, r.values.email].filter(Boolean).join(' · ');
      const rd = r.reading || {};
      return [rd.date ? fmtDate(rd.date) : null, rd.identifier].filter(Boolean).join(' · ');
    };

    // ── Grupos ──────────────────────────────────────────────────────
    const optsFor = (g) => options.value.byKind?.[g.kind] || options.value;
    const groupFields = (g) => GROUP_FIELDS
      .filter(f => !(f.key === 'water_source' && g.kind !== 'water'))
      .filter(f => !(f.key === 'vehicleActivityId' && g.kind !== 'fuel'))
      .map(f => ({ ...f, options: optsFor(g)[f.options] || [] }))
      .filter(f => f.options.length);
    const groupTarget = (g) => groupFields(g)
      .map(f => f.options.find(o => String(o.value) === String(g.target?.[f.key]))?.label)
      .filter(Boolean).join(' · ');
    const startGroup = (g) => {
      Object.keys(groupEdit).forEach(k => delete groupEdit[k]);
      groupFields(g).forEach((f) => { const v = g.target?.[f.key]; groupEdit[f.key] = v == null ? '' : String(v); });
      editingGroup.value = g.gid;
    };
    const saveGroup = (g) => {
      const changes = {};
      groupFields(g).forEach((f) => {
        const now = groupEdit[f.key];
        if (now === '' || String(now) === String(g.target?.[f.key] ?? '')) return;
        changes[f.key] = f.key === 'water_source' ? now : Number(now);
      });
      editingGroup.value = null;
      if (Object.keys(changes).length) emit('patch', { groups: { [g.gid]: changes } });
    };

    return {
      LIMIT, STRONG, showAll, editingGroup, groupEdit,
      rows, groups, shownRows, columns, summaryText, mappingEntries, fieldLabel,
      rowPrimary, rowSecondary, groupFields, groupTarget, startGroup, saveGroup,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/assistant/draft-sheet";
</style>
